import { lazy, Suspense, useEffect, useState, type ReactNode } from "react"
import { useRouter } from "next/router"

import { ExpandableAudioPlayer, useAudioSignedUrlTransport } from "@/audio"
import {
  useRunSimulationsKeysetTransport,
  useSimulationOutputTransport,
} from "@/runs"
import { useTraceAudioContextTransport } from "@/traces"
import { useConversationsKeysetInfiniteTransport } from "@/uploaded-conversations/browse"
import { Alert, Button, CopyableId, Loader } from "@coval/ui"
import { ArrowLeftRight, Info, Play, RefreshCw } from "lucide-react"

import { recordingTranscriptNotice } from "@/components/audio-player/recordingTranscriptNotice"
import type { TestSqlMetricSimulationResult } from "@/types_api/backend/transport/test_recent_sql_metric_response"

import { SqlConversationPickerDialog } from "../components/SqlConversationPickerDialog"
import { formatNullableMetricValue } from "../utils/sqlMetricValue"

const SqlConversationPicker = lazy(() =>
  import("./SqlConversationPicker").then((module) => ({
    default: module.SqlConversationPicker,
  })),
)

const RECENT_REQUEST = {
  limit: 1,
  sort_column: "createdAt",
  sort_direction: "DESC",
} as const
const UPLOAD_REQUEST = {
  limit: 1,
  filters: { monitoring_filter: true },
} as const

interface SqlSimulationPreviewProps {
  preferredSimulationId?: string | null
  onSimulationChange?: (simulationOutputId: string | null) => void
  results?: TestSqlMetricSimulationResult[]
  renderResult?: (result?: TestSqlMetricSimulationResult) => ReactNode
  onTest?: () => void
  isTesting?: boolean
  canTest?: boolean
}

export function SqlSimulationPreview(props: SqlSimulationPreviewProps) {
  const router = useRouter()
  return (
    <ScopedSqlSimulationPreview key={router.asPath.split("?")[0]} {...props} />
  )
}

function ScopedSqlSimulationPreview({
  results,
  renderResult,
  preferredSimulationId,
  onSimulationChange,
  onTest,
  isTesting,
  canTest = true,
}: SqlSimulationPreviewProps) {
  const recent = useRunSimulationsKeysetTransport({
    enabled: true,
    request: RECENT_REQUEST,
  })
  const uploads = useConversationsKeysetInfiniteTransport({
    enabled: true,
    request: UPLOAD_REQUEST,
  })
  const [requestedId, setRequestedId] = useState<string>()
  const [requestedSource, setRequestedSource] = useState<
    "simulated" | "uploaded"
  >()
  const [pickerOpen, setPickerOpen] = useState(false)
  const sourcesReady =
    !recent.isPending &&
    !uploads.isPending &&
    !recent.isPlaceholderData &&
    !uploads.isPlaceholderData
  const candidates = [
    ...(recent.isPlaceholderData
      ? []
      : (recent.data?.pages
          .flatMap((page) => page.items)
          .map((row) => ({
            id: row.simulation_output_id,
            date: row.created_at,
            source: "simulated" as const,
          })) ?? [])),
    ...(uploads.isPlaceholderData
      ? []
      : (uploads.data?.pages
          .flatMap((page) => page.items)
          .filter((row) => row.simulation_output_id)
          .map((row) => ({
            id: row.simulation_output_id!,
            date: row.run_created_at,
            source: "uploaded" as const,
          })) ?? [])),
  ].sort(
    (a, b) =>
      (Date.parse(b.date ?? "") || 0) - (Date.parse(a.date ?? "") || 0) ||
      b.id.localeCompare(a.id),
  )
  const selectedId =
    requestedId ??
    preferredSimulationId ??
    (sourcesReady && !recent.error && !uploads.error
      ? candidates[0]?.id
      : undefined) ??
    null
  const selectedSource =
    requestedSource ??
    candidates.find((candidate) => candidate.id === selectedId)?.source
  const selectedResult = results?.find(
    (result) => result.simulation_output_id === selectedId,
  )
  useEffect(() => {
    onSimulationChange?.(selectedId)
  }, [onSimulationChange, selectedId])
  return (
    <section
      aria-label="Conversation audio preview"
      className="min-w-0 space-y-2"
    >
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2 text-xs text-coval-text-muted">
          <span>
            {selectedSource === "uploaded"
              ? "Uploaded conversation"
              : selectedSource === "simulated"
                ? "Simulated conversation"
                : "Conversation preview"}
          </span>
          {selectedId && (
            <CopyableId id={selectedId} label="Copy Conversation ID" />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setPickerOpen(true)}
          >
            <ArrowLeftRight />
            Select Conversation
          </Button>
          <div className="flex shrink-0 items-center gap-2">
            {onTest && (
              <Button
                data-metric-editor-action
                type="button"
                variant="secondary"
                className="w-32 shrink-0"
                aria-label="Test Metric"
                onClick={onTest}
                disabled={!canTest || !selectedId || isTesting}
                aria-busy={isTesting || undefined}
              >
                <Play className="shrink-0" />
                Test Metric
              </Button>
            )}
            {renderResult?.(!isTesting ? selectedResult : undefined)}
          </div>
        </div>
      </div>
      {(recent.error || uploads.error) && (
        <div
          role="alert"
          className="flex items-center justify-between gap-2 text-sm text-coval-danger-on-surface"
        >
          Couldn&apos;t load the latest conversation.
          <Button
            variant="secondary"
            onClick={() => {
              void recent.refetch()
              void uploads.refetch()
            }}
          >
            Retry conversations
          </Button>
        </div>
      )}
      {!selectedId &&
        !recent.error &&
        !uploads.error &&
        (!sourcesReady ? (
          <Loader label="Loading latest conversation" />
        ) : (
          <p role="status" className="text-sm text-coval-text-muted">
            Upload a conversation or run a simulation to preview this metric.
          </p>
        ))}
      {selectedId && (
        <SqlSimulationAudio
          key={selectedId}
          simulationOutputId={selectedId}
          result={selectedResult}
        />
      )}
      {pickerOpen && (
        <SqlConversationPickerDialog onClose={() => setPickerOpen(false)}>
          <Suspense
            fallback={
              <div className="flex min-h-0 flex-1 items-center justify-center gap-2 text-sm text-coval-text-muted">
                <Loader label="Loading conversations…" />
                <span>Loading conversations…</span>
              </div>
            }
          >
            <SqlConversationPicker
              selectedId={selectedId}
              onSelect={(id, source) => {
                setRequestedSource(source)
                setRequestedId(id)
                setPickerOpen(false)
              }}
            />
          </Suspense>
        </SqlConversationPickerDialog>
      )}
    </section>
  )
}

function SqlSimulationAudio({
  simulationOutputId,
  result,
}: {
  simulationOutputId: string
  result?: TestSqlMetricSimulationResult
}) {
  const simulation = useSimulationOutputTransport(simulationOutputId, {
    refetchOnWindowFocus: false,
  })
  const row = simulation.data
  const hasRecording = Boolean(
    row?.simulation_s3_bucket && row.simulation_s3_key,
  )
  const audioContext = useTraceAudioContextTransport(simulationOutputId, {
    enabled: hasRecording,
    refetchOnWindowFocus: false,
  })
  const audio = useAudioSignedUrlTransport(
    {
      bucket: row?.simulation_s3_bucket ?? "",
      audioKey: row?.simulation_s3_key ?? "",
    },
    { enabled: hasRecording, refetchOnWindowFocus: false },
  )
  const [audioAttempt, setAudioAttempt] = useState(0)
  const [loadError, setLoadError] = useState<Error | null>(null)
  if (simulation.error && !row) {
    return (
      <PreviewError
        message="Couldn't load this conversation."
        onRetry={() => {
          void simulation.refetch()
        }}
      />
    )
  }
  if (simulation.isLoading || simulation.isPending) {
    return (
      <p role="status" className="text-sm text-coval-text-muted">
        Loading recording…
      </p>
    )
  }
  if (!row) {
    return (
      <PreviewError
        message="This conversation is no longer available."
        onRetry={() => {
          void simulation.refetch()
        }}
      />
    )
  }
  return (
    <div className="space-y-2">
      {simulation.error && (
        <PreviewError
          message="Couldn't refresh this conversation."
          onRetry={() => {
            void simulation.refetch()
          }}
        />
      )}
      {(audio.error || loadError) && (
        <PreviewError
          message="Couldn't load this recording."
          onRetry={() => {
            setLoadError(null)
            setAudioAttempt((attempt) => attempt + 1)
            void audio.refetch()
          }}
        />
      )}
      {audio.isLoading && hasRecording && (
        <p role="status" className="text-sm text-coval-text-muted">
          Loading recording…
        </p>
      )}
      {!hasRecording && (
        <Alert variant="warning" icon={<Info />} className="text-xs">
          This preview is only available for conversations with audio.
        </Alert>
      )}
      {hasRecording && (
        <ExpandableAudioPlayer
          key={audioAttempt}
          signedUrl={audio.data}
          recordingKey={[
            simulationOutputId,
            row.simulation_s3_bucket,
            row.simulation_s3_key,
          ].join("::")}
          audioDuration={audioContext.data?.audioFileDurationSeconds}
          simulationOutputId={simulationOutputId}
          canInspectFrames
          transcriptNotice={recordingTranscriptNotice(row)}
          onLoadError={setLoadError}
          timedValues={
            result?.error
              ? []
              : (result?.subvalues?.map((point) => ({
                  startSeconds: point.start_offset_seconds,
                  endSeconds: point.end_offset_seconds,
                  label: formatNullableMetricValue(point.value, result.unit),
                  displayValue: formatNullableMetricValue(point.value),
                })) ?? [])
          }
        />
      )}
    </div>
  )
}

function PreviewError({
  message,
  onRetry,
}: {
  message: string
  onRetry: () => void
}) {
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-2 text-sm text-coval-danger-on-surface"
    >
      {message}
      <Button type="button" variant="secondary" onClick={onRetry}>
        <RefreshCw />
        Retry recording
      </Button>
    </div>
  )
}
